import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { pmApi } from '../../api/pm.js';

/**
 * Rich-text description editor for the issue detail page.
 *
 * - TipTap with StarterKit (bold, italic, strike, code, headings, lists,
 *   blockquote, code block, undo/redo) + Placeholder.
 * - Toolbar above the content area; click anywhere in the editor to focus.
 * - Autosave on blur via PUT /issues/:id  body { description: <html> }.
 * - "Saving..." → "Saved" indicator in the top-right.
 * - Escape key reverts unsaved changes (restores the last saved value).
 *
 * @mention support is intentionally out of scope here — the existing
 * MentionInput component handles @-mentions in comments. Add an @-mention
 * suggestion plugin later if you want them in descriptions too.
 */
export default function IssueDescription({ issueId, initialHtml }) {
  const lastSavedRef = useRef(initialHtml || '');
  const [status, setStatus] = useState('idle'); // idle | saving | saved | error
  const [readOnly] = useState(false);

  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Add a description…' }),
    ],
    content: initialHtml || '',
    editorProps: {
      attributes: {
        class: 'tiptap-editor',
        style: 'min-height: 120px; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 4px; outline: none;',
      },
      handleKeyDown(_view, event) {
        if (event.key === 'Escape' && editor) {
          event.preventDefault();
          editor.commands.setContent(lastSavedRef.current);
          editor.commands.blur();
          return true;
        }
        return false;
      },
    },
    onBlur: ({ editor }) => save(editor.getHTML()),
  });

  // Reload when issueId changes
  useEffect(() => { lastSavedRef.current = initialHtml || ''; if (editor) editor.commands.setContent(initialHtml || ''); }, [issueId, initialHtml, editor]);

  async function save(html) {
    if (!issueId) return;
    if (html === lastSavedRef.current) return; // no change
    setStatus('saving');
    try {
      await pmApi.updateIssue(issueId, { description: html });
      lastSavedRef.current = html;
      setStatus('saved');
      // Fade back to idle.
      setTimeout(() => setStatus((s) => (s === 'saved' ? 'idle' : s)), 1500);
    } catch {
      setStatus('error');
    }
  }

  if (!editor) return null;

  return (
    <div style={{ position: 'relative' }}>
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
      <div
        aria-live="polite"
        style={{ position: 'absolute', top: 4, right: 8, fontSize: 11, color: status === 'error' ? '#dc2626' : '#64748b' }}
      >
        {status === 'saving' && 'Saving…'}
        {status === 'saved' && 'Saved'}
        {status === 'error' && 'Save failed'}
      </div>
    </div>
  );
}

function Toolbar({ editor }) {
  const btn = (active, label, onClick, title) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className={'btn ghost small ' + (active ? 'is-active' : '')}
      style={{ background: active ? '#e2e8f0' : 'transparent', padding: '2px 8px', fontSize: 12 }}
      title={title}
      aria-label={title}
    >
      {label}
    </button>
  );

  return (
    <div className="tiptap-toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
      {btn(editor.isActive('bold'), <b>B</b>, () => editor.chain().focus().toggleBold().run(), 'Bold')}
      {btn(editor.isActive('italic'), <i>I</i>, () => editor.chain().focus().toggleItalic().run(), 'Italic')}
      {btn(editor.isActive('strike'), <s>S</s>, () => editor.chain().focus().toggleStrike().run(), 'Strikethrough')}
      {btn(editor.isActive('code'), <code>{'<>'}</code>, () => editor.chain().focus().toggleCode().run(), 'Inline code')}
      <span style={{ width: 1, background: '#e2e8f0', margin: '0 4px' }} />
      {btn(editor.isActive('heading', { level: 2 }), 'H2', () => editor.chain().focus().toggleHeading({ level: 2 }).run(), 'Heading 2')}
      {btn(editor.isActive('heading', { level: 3 }), 'H3', () => editor.chain().focus().toggleHeading({ level: 3 }).run(), 'Heading 3')}
      <span style={{ width: 1, background: '#e2e8f0', margin: '0 4px' }} />
      {btn(editor.isActive('bulletList'), '• List', () => editor.chain().focus().toggleBulletList().run(), 'Bullet list')}
      {btn(editor.isActive('orderedList'), '1. List', () => editor.chain().focus().toggleOrderedList().run(), 'Ordered list')}
      {btn(editor.isActive('blockquote'), '❝', () => editor.chain().focus().toggleBlockquote().run(), 'Blockquote')}
      {btn(editor.isActive('codeBlock'), '{ }', () => editor.chain().focus().toggleCodeBlock().run(), 'Code block')}
      <span style={{ width: 1, background: '#e2e8f0', margin: '0 4px' }} />
      {btn(false, '↶', () => editor.chain().focus().undo().run(), 'Undo')}
      {btn(false, '↷', () => editor.chain().focus().redo().run(), 'Redo')}
    </div>
  );
}
